import React, {FC} from 'react';

import {useLocalize} from '../../../../../libs/shared/core/src/helpers/internationalization/internationalizationContext';
import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {Renderer} from '../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {
  DraftMessageModel,
  TimelineMessageModel,
} from '../../../../../libs/shared/core/src/models/api/messageModel';
import {RecipientRolesEnum} from '../../../../../libs/shared/core/src/models/api/recipientModel';
import {TimelineVariationsEnum} from '../../../../../libs/shared/core/src/store/timelineVariations/timelineVariationsTypes';
import {buildReplyQuote, buildReplyQuoteWithHeaders} from '../../../../helpers/models/messageHelpers';
import {MessagePatch} from '../../../../helpers/models/messagePatchHelpers';
import {VisualRecipientModel} from '../../../../models/api/visualRecipientModel';
import {ComposerSectionEditorCoordinator} from '../common/composerSectionEditorCoordinator';
import {EmailComposerStatic} from '../common/emailComposerStatic';

/*
 * Props.
 */

export interface QuoteProps {
  message: DraftMessageModel;
  isQuoteVisible: boolean;
  onToggleIsQuoteVisible: () => void;
  isAlwaysEditing: boolean;
  replyToMessage: TimelineMessageModel | undefined;
  replyToRecipients: Array<VisualRecipientModel> | undefined;
  shouldReplyWithOutlookStyle: boolean;
  saveMessageAsync: (patch: MessagePatch) => void;
  renderEditor: Renderer;
  hasFocus?: boolean;
}

/*
 * Component.
 */

export const Quote: FC<QuoteProps> = ({
  message,
  isQuoteVisible,
  onToggleIsQuoteVisible,
  isAlwaysEditing,
  replyToMessage,
  replyToRecipients,
  shouldReplyWithOutlookStyle,
  saveMessageAsync,
  renderEditor,
  hasFocus,
}) => {
  const localize = useLocalize();

  const onAdd = () => saveMessageAsync({newIsQuoteIncluded: true});
  const onRemove = () => saveMessageAsync({newIsQuoteIncluded: false});
  const onEdit = () => saveMessageAsync({newWasQuoteModified: true});
  const onRevert = () => {
    if (!replyToMessage || !replyToRecipients) {
      return;
    }

    const recipient = replyToRecipients.find((r) => r.role === RecipientRolesEnum.FROM);
    if (!recipient) {
      saveMessageAsync({
        newQuoteHtml: undefined,
        newWasQuoteModified: false,
      });
      return;
    }

    const newQuoteHtml = shouldReplyWithOutlookStyle
      ? buildReplyQuoteWithHeaders(localize, replyToMessage, replyToRecipients)
      : buildReplyQuote(localize, replyToMessage, recipient);

    saveMessageAsync({
      newQuoteHtml,
      newWasQuoteModified: false,
    });
  };

  if (!message.quoteHtml) {
    return null;
  }

  return (
    <ComposerSectionEditorCoordinator
      isIncluded={Boolean(message.isQuoteIncluded)}
      onAdd={onAdd}
      onRemove={onRemove}
      renderAddLabel={() => <Trans id="composer-quote-add">Add previous message</Trans>}
      renderRemoveLabel={() => <Trans id="composer-quote-remove">Remove previous message</Trans>}
      canHide
      isVisible={isQuoteVisible}
      onToggleIsVisible={onToggleIsQuoteVisible}
      renderShowLabel={() => <Trans id="composer-quote-show">Show previous message</Trans>}
      renderHideLabel={() => <Trans id="composer-quote-hide">Hide previous message</Trans>}
      renderStatic={() => (
        <EmailComposerStatic
          shouldAddQuoteStyle
          html={message.quoteHtml}
          timelineVariation={TimelineVariationsEnum.ASYNCHRONOUS}
        />
      )}
      isAlwaysEditing={isAlwaysEditing}
      isEditing={Boolean(message.wasQuoteModified)}
      onEdit={onEdit}
      onRevert={onRevert}
      renderEditor={renderEditor}
      hasFocus={hasFocus}
    />
  );
};
